import { isNil, isNotNil, isString } from '@flowio/is';
import storage from '@flowio/storage';
import { parseQueryString } from '@flowio/web-sdk';
import { getApiClient } from '../services';
import getCookieDomain from './getCookieDomain';
import getRollbarInstance from './getRollbarInstance';

type FlowSessionModifierParams = {
  flow_country?: string;
  flow_experience?: string;
};

type CreateSessionParams = {
  country?: string;
  /**
   * @description Flow experience key to set on the user session
   */
  experience?: string;
  organization: string;
  /**
   * @description iso3166-1 alpha 3 country code (i.e. 'CAN')
   */
  sessionId?: string;
};

type GetSessionParams = Partial<CreateSessionParams> & {
  // used to infer what sessionId to use if none already exists.
  sessionId?: string;
};

function isOrganizationSession(
  session: unknown,
): session is io.flow.v0.models.OrganizationSession {
  return isNotNil(session) && isNotNil((session as io.flow.v0.models.OrganizationSession).discriminator === 'organization_session');
}

function isCreateSessionParams(params: GetSessionParams): params is CreateSessionParams {
  return isNotNil((params as CreateSessionParams).organization);
}

function getLocalStoredSession(): io.flow.v0.models.OrganizationSession | null {
  const storedSession = storage.session.get('session')
    || storage.local.get('session')
    || storage.memory.get('session');

  if (isOrganizationSession(storedSession)) {
    return storedSession;
  }
  return null;
}

function getLocalStoredSessionId(): string | null {
  const storedCookieId = storage.cookie.get('session');

  return isString(storedCookieId) ? storedCookieId : null;
}

function cleanCookie(): void {
  storage.cookie.remove('session');
}

function persistSession(session: io.flow.v0.models.OrganizationSession): void {
  cleanCookie();
  const domain = getCookieDomain();

  storage.cookie.set('session', session.id, {
    domain,
    expires: 31536000, // year in seconds
  });

  // set local (which may fail if WebStorage access is denied)
  storage.local.set('session', session);
  // set memory storage also incase WebStorage set fails.
  storage.memory.set('session', session);
}

function generateSessionPutForm(
  params: GetSessionParams,
  session: io.flow.v0.models.OrganizationSession,
): io.flow.v0.models.SessionPutForm {
  const country = params.country
    || session.local?.country.iso_3166_3
    || session.geo?.country.iso_3166_3;
  return {
    country,
    experience: params.experience || session.experience?.key,
  };
}

function createSession(
  params: CreateSessionParams,
): Promise<io.flow.v0.models.OrganizationSession> {
  const queryParams = parseQueryString<FlowSessionModifierParams>(window.location.search);
  return getApiClient().session.postOrganizationsByOrganization({
    organization: params.organization,
    body: {
      country: queryParams.flow_country || params.country,
      experience: queryParams.flow_experience || params.experience,
    },
  }).then((response) => {
    if (response.ok) {
      persistSession(response.body);
      return response.body;
    }
    getRollbarInstance().error('SDK: createSession: failed to create session.');
    throw new Error('createSession: failed to create session.');
  });
}

function ensureFresh(
  params: GetSessionParams,
  session: io.flow.v0.models.OrganizationSession,
): Promise<io.flow.v0.models.OrganizationSession> {
  const expiryGracePeriod = 5 * 60 * 1000;
  const expiry = session.visit.expires_at;
  if (isNotNil(expiry)) {
    const expiryDate = new Date(expiry);
    if (expiryDate.valueOf() - Date.now() < expiryGracePeriod) {
      return getApiClient().session.putResetBySession({
        session: session.id,
        body: generateSessionPutForm(params, session),
      }).then((response) => {
        if (response.ok) {
          persistSession(response.body);
          return response.body;
        }
        if (isCreateSessionParams(params)) {
          return createSession(params);
        }
        getRollbarInstance().error('SDK: ensureFresh: failed to refresh session.');
        throw new Error('ensureFresh: failed to refresh session.');
      });
    }
  }
  return Promise.resolve(session);
}

export default function getSession(
  params: GetSessionParams = {},
): Promise<io.flow.v0.models.OrganizationSession> {
  return Promise.resolve().then(() => {
    const session = getLocalStoredSession();

    if (isNotNil(session)) {
      if (isNotNil(params.sessionId) && session.id === params.sessionId) {
        // if sessionId was passed, only return the stored session object if it matches passed Id.
        return ensureFresh(params, session);
      }
      // Organization mis-match checks are mainly for internal use.
      if (isNotNil(params.organization) && session.organization === params.organization) {
        // if organization was passed, only return the stored session object if it matches passed
        // organization.
        return ensureFresh(params, session);
      }
      // we're not opinionated about what the session should be, just requesting what we got.
      if (isNil(params.organization) && isNil(params.sessionId)) {
        return ensureFresh(params, session);
      }
    }

    const sessionId = params.sessionId || getLocalStoredSessionId();
    if (isNotNil(sessionId)) {
      return getApiClient().session.getBySession({ session: sessionId }).then((response) => {
        if (response.ok) {
          persistSession(response.body);
          return response.body;
        }
        return undefined;
      }).then((fetchedSession) => {
        if (isNotNil(fetchedSession) && (isNil(params.organization)
            || fetchedSession.organization === params.organization)) {
          return ensureFresh(params, fetchedSession);
        }
        if (isCreateSessionParams(params)) {
          return createSession(params);
        }
        getRollbarInstance().error(`SDK: getSession: could not fetch or create a session from params: ${JSON.stringify(params)}`);
        throw new Error(`SDK: getSession: could not fetch or create a session from params: ${JSON.stringify(params)}`);
      });
    }

    if (isCreateSessionParams(params)) {
      return createSession(params);
    }

    getRollbarInstance().error('SDK: getSession: no session found and missing organization.');
    throw new Error('SDK: getSession: no session found and missing organization.');
  });
}
